import { PDFName, PDFHexString, PDFString, PDFArray, PDFContentStream } from "pdf-lib";

const pdfDate = (iso) => {
    const d = iso ? new Date(iso) : new Date();
    const p = (n) => String(n).padStart(2, "0");
    return PDFString.of(`D:${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}Z`);
};
export const txt = (s) => PDFHexString.fromText(String(s ?? ""));

// Native (Acrobat) annotations with popups, replies and review state.
export function createAnnotator(pdf) {
    const ctx = pdf.context;
    const push = (page, ref) => {
        let a = page.node.lookupMaybe(PDFName.of("Annots"), PDFArray);
        if (!a) { a = ctx.obj([]); page.node.set(PDFName.of("Annots"), a); }
        a.push(ref);
    };
    const add = (page, fields) => { const ref = ctx.register(ctx.obj(fields)); push(page, ref); return ref; };
    const emptyAP = (rect) => ctx.register(PDFContentStream.of(ctx.obj({ Type: "XObject", Subtype: "Form", BBox: rect }), []));

    // Renders draw(tmpPage) into a Form XObject in the same coordinate space -> identical appearance.
    const apFrom = async (page, draw) => {
        const b = page.getCropBox();
        const tmp = pdf.addPage([b.width, b.height]);
        tmp.setMediaBox(b.x, b.y, b.width, b.height);
        const rect = await draw(tmp);
        const cs = tmp.getContentStream();
        cs.dict.set(PDFName.of("Type"), PDFName.of("XObject"));
        cs.dict.set(PDFName.of("Subtype"), PDFName.of("Form"));
        cs.dict.set(PDFName.of("BBox"), ctx.obj(rect));
        cs.dict.set(PDFName.of("Resources"), tmp.node.Resources());
        const ref = tmp.contentStreamRef;
        pdf.removePage(pdf.getPageCount() - 1);
        return { ref, rect };
    };

    const addMarkup = (page, a) => {
        const dict = {
            Type: "Annot", Subtype: a.subtype, Rect: a.rect, Contents: txt(a.contents), T: txt(a.author), Subj: txt(a.subject || a.subtype),
            M: pdfDate(a.date), CreationDate: pdfDate(a.date), NM: txt(a.id), F: 4, AP: { N: a.ap }, ...(a.extra || {}),
        };
        if (a.color) dict.C = a.color;
        if (a.opacity !== undefined) dict.CA = a.opacity;
        const ref = add(page, dict);
        const pb = page.getCropBox();
        const pw = 220, ph = 130;
        const px = a.rect[2] + pw > pb.x + pb.width ? Math.max(pb.x, a.rect[0] - pw) : a.rect[2];
        const py = Math.max(pb.y, Math.min(a.rect[3], pb.y + pb.height) - ph);
        const popup = add(page, { Type: "Annot", Subtype: "Popup", Rect: [px, py, px + pw, py + ph], Parent: ref, Open: !!a.open, F: 24 });
        ctx.lookup(ref).set(PDFName.of("Popup"), popup);
        const small = [a.rect[0], a.rect[3] - 1, a.rect[0] + 1, a.rect[3]];
        (a.replies || []).forEach((r, i) => add(page, {
            Type: "Annot", Subtype: "Text", Rect: small, Contents: txt(r.text), T: txt(r.author?.name), M: pdfDate(r.created_at),
            NM: txt(`${a.id}-r${i}`), IRT: ref, RT: "R", Name: "Comment", F: 4, AP: { N: emptyAP(small) },
        }));
        if (a.resolved) add(page, {
            Type: "Annot", Subtype: "Text", Rect: small, Contents: txt(a.resolvedText || "Accepted"), T: txt(a.author), M: pdfDate(),
            NM: txt(`${a.id}-state`), IRT: ref, StateModel: txt("Review"), State: txt("Accepted"), Name: "Comment", F: 30, AP: { N: emptyAP(small) },
        });
        return ref;
    };
    return { addMarkup, emptyAP, apFrom, ctx };
}
